// Gemeinsame Toast-Logik für fällige Erinnerungen (ReminderWatcher + Socket-Ereignis NOTIFICATION_CREATE).
import type { ReminderDto } from '@lagerfeuer/shared';
import type { QueryClient } from '@tanstack/react-query';
import { messageLink, remindersApi } from '../api';
import { errorMessage } from '../format';
import { toast } from '../toast';
import { excerpt } from '../scheduled/time';

/** Präfix aller Erinnerungs-Queries. */
export const REMINDERS_KEY = ['extras', 'reminders'] as const;
export const remindersListKey = (done: boolean) => [...REMINDERS_KEY, done ? 'done' : 'open'] as const;

/**
 * Bereits gezeigte Erinnerungen dieser Sitzung. Schlüssel immer `<id>@<remindAt>` – eine verschobene Erinnerung
 * meldet sich zum neuen Zeitpunkt erneut (nie nur `<id>`, das würde alle späteren Zeitpunkte unterdrücken).
 */
const shown = new Set<string>();

const shownKey = (id: string, remindAt: string) => `${id}@${remindAt}`;

export function wasReminderShown(id: string, remindAt: string): boolean {
  return shown.has(shownKey(id, remindAt));
}

export function markReminderShown(id: string, remindAt: string): void {
  shown.add(shownKey(id, remindAt));
}

/** Nur für Tests: Sitzungsgedächtnis leeren. */
export function resetShownReminders(): void {
  shown.clear();
}

export const reminderToastId = (id: string) => `reminder-${id}`;

/** „Erledigt" aus dem Toast: PATCH done:true und Listen neu laden. */
export function markReminderDone(queryClient: QueryClient, id: string): void {
  remindersApi
    .update(id, { done: true })
    .then(() => queryClient.invalidateQueries({ queryKey: REMINDERS_KEY }))
    .catch((err: unknown) => {
      toast({ kind: 'error', title: 'Erinnerung konnte nicht erledigt werden', body: errorMessage(err) });
    });
}

/** Toast für eine fällige Erinnerung (gleiche Toast-ID verhindert doppelte Anzeige). */
export function showReminderToast(
  queryClient: QueryClient,
  r: { id: string; note: string | null; text?: string | null; href?: string; canComplete?: boolean },
): void {
  toast({
    kind: 'reminder',
    id: reminderToastId(r.id),
    title: 'Erinnerung',
    body: r.note?.trim() || excerpt(r.text) || 'Eine Erinnerung ist fällig.',
    href: r.href,
    action: r.canComplete === false ? undefined : { label: 'Erledigt', run: () => markReminderDone(queryClient, r.id) },
  });
}

/** Toast-Daten aus einem ReminderDto. */
export function reminderToastData(r: ReminderDto) {
  const m = r.message;
  const href = m ? messageLink(m.serverId, m.channelId, m.id) : undefined;
  return { id: r.id, note: r.note, text: m && !m.deleted ? m.content : null, href };
}
